/**
 * W5 — pure builder for "View <Technician>'s Bays" (mockup panel 3).
 *
 * Input is the server read model `technician_workspace_day` (migration 20270206072000) VERBATIM. This file only
 * lays the server's blocks on a time axis; it never synthesises a state, never sums hands-on with unattended time,
 * and never draws a planned or predicted time as if it were measured.
 *
 *   labor row     ← blocks.kind = 'hands_on' (measured; the ONLY labor-credited kind; never overlapping)
 *   vehicle lanes ← one lane per vehicle, labelled with its CURRENT bay (and that bay's basis), carrying the
 *                   vehicle's hands-on, unattended, switched-away, paused and planned blocks
 */

export type WorkspaceBlockKind = 'hands_on' | 'unattended' | 'switched_away' | 'paused' | 'planned';

export interface WorkspaceBlockDto {
  id: string;
  kind: WorkspaceBlockKind;
  activity: 'ticket_work' | 'inspection' | null;
  ticket_id: string;
  ticket_number: number | null;
  vehicle_label: string | null;
  start_at: string;
  end_at: string | null;
  open: boolean;
  time_basis: 'measured' | 'planned';
  labor_credit: boolean;
  source: string;
  pause_intent: string | null;
  pause_reason: string | null;
  unattended_kind: string | null;
  expected_end_at: string | null;
  expected_end_basis: 'stated' | 'none' | null;
  bay_id: string | null;
  continues_from_previous_day: boolean;
  continues_next_day: boolean;
}

export interface WorkspaceVehicleDto {
  ticket_id: string;
  ticket_number: number | null;
  vehicle_label: string | null;
  status: string;
  state: string;
  bay_id: string | null;
  bay_basis: 'physical_current' | 'legacy_projection' | 'planned' | 'none';
  planned_start_at: string | null;
  predicted_finish_at: string | null;
  predicted_finish_basis: 'predicted' | 'none';
  open_pause_intent: string | null;
  open_unattended: Array<{
    id: string; kind: string; kind_note: string | null; started_at: string; expected_minutes: number | null;
    expected_end_at: string | null; past_expected_end: boolean; started_by_technician_id: string; bay_id_at_start: string | null;
  }>;
}

export interface TechnicianWorkspaceDayDto {
  available: boolean;
  technician?: { id: string; name: string };
  viewer_is_technician?: boolean;
  shop_id?: string;
  day?: string;
  time_zone?: string;
  day_start?: string;
  day_end?: string;
  as_of?: string;
  active?: { source: 'ticket_timer' | 'inspection_timer'; session_id: string; ticket_id: string; ticket_number: number | null; started_at: string } | null;
  predicted_next_available?: { at: string | null; basis: 'predicted' | 'none' | 'free_now'; source?: string };
  blocks?: WorkspaceBlockDto[];
  vehicles?: WorkspaceVehicleDto[];
  bays?: Record<string, { name: string; bay_number: number | null }>;
  totals?: { hands_on_minutes: number; unattended_minutes: number; switched_wait_minutes: number };
}

export interface TimelineBlock {
  id: string;
  kind: WorkspaceBlockKind;
  ticketId: string;
  startMs: number;
  /** For an open block: the "as of" instant (it is still running). */
  endMs: number;
  open: boolean;
  timeBasis: 'measured' | 'planned';
  laborCredit: boolean;
  leftPct: number;
  widthPct: number;
  /** Screen-reader label: words, never colour alone. */
  label: string;
  expectedEndMs: number | null;
  conflict: boolean;
}

export interface VehicleLane {
  ticketId: string;
  ticketNumber: number | null;
  vehicleLabel: string | null;
  bayName: string | null;
  bayBasis: WorkspaceVehicleDto['bay_basis'];
  state: string;
  stateLabel: string;
  predictedFinishMs: number | null;
  blocks: TimelineBlock[];
  openUnattended: WorkspaceVehicleDto['open_unattended'];
}

export interface WorkspaceTimeline {
  axisStartMs: number;
  axisEndMs: number;
  ticks: Array<{ ms: number; label: string; leftPct: number }>;
  nowMs: number;
  nowLeftPct: number | null;
  laborRow: TimelineBlock[];
  lanes: VehicleLane[];
  /** Vehicles physically in a bay right now (current or legacy-projected physical truth only). */
  vehiclesInBays: number;
  distinctBays: number;
  /** True when two measured hands-on blocks overlap — impossible by the DB constraint; shown, never hidden. */
  laborOverlapDetected: boolean;
}

export function vehicleStateLabel(state: string): string {
  switch (state) {
    case 'HANDS_ON': return 'Hands-on now';
    case 'UNATTENDED_RUNNING': return 'Machine running (unattended)';
    case 'SWITCHED_AWAY': return 'Switched away — waiting for technician';
    case 'NOT_STARTED': return 'Not started';
    case 'STOPPED': return 'Stopped';
    case 'DONE': return 'Done';
    default:
      if (state.startsWith('PAUSED_')) {
        const why = state.slice('PAUSED_'.length).toLowerCase().replace(/_/g, ' ');
        return why === 'unstated' ? 'Paused' : `Paused · ${why}`;
      }
      return state;
  }
}

const KIND_WORDS: Record<WorkspaceBlockKind, string> = {
  hands_on: 'Hands-on, measured',
  unattended: 'Unattended machine time, measured, no labor credit',
  switched_away: 'Switched away, measured wait, no labor credit',
  paused: 'Paused, measured, no labor credit',
  planned: 'Planned',
};

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

export function formatShopTime(msValue: number, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone }).format(new Date(msValue));
  } catch {
    return new Date(msValue).toISOString().slice(11, 16);
  }
}

export function buildTechnicianWorkspace(dto: TechnicianWorkspaceDayDto, nowMsArg?: number): WorkspaceTimeline | null {
  if (!dto.available || !dto.day_start || !dto.day_end) return null;
  const tz = dto.time_zone || 'UTC';
  const dayStart = ms(dto.day_start)!;
  const dayEnd = ms(dto.day_end)!;
  const nowMs = nowMsArg ?? ms(dto.as_of) ?? Date.now();
  const blocks = dto.blocks ?? [];
  const vehicles = dto.vehicles ?? [];

  // Axis: the span of every block (open ones until now), padded to whole hours, clamped to the shop-local day.
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const b of blocks) {
    const s = ms(b.start_at);
    const e = b.end_at ? ms(b.end_at) : b.kind === 'planned' ? s : nowMs;
    if (s != null) lo = Math.min(lo, s);
    if (e != null) hi = Math.max(hi, e);
    const x = ms(b.expected_end_at);
    if (x != null) hi = Math.max(hi, x);
  }
  if (nowMs >= dayStart && nowMs <= dayEnd) { lo = Math.min(lo, nowMs); hi = Math.max(hi, nowMs); }
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) { lo = dayStart + 8 * 3_600_000; hi = dayStart + 17 * 3_600_000; }
  const HOUR = 3_600_000;
  lo = Math.max(dayStart, Math.floor((lo - 30 * 60_000) / HOUR) * HOUR);
  hi = Math.min(dayEnd, Math.ceil((hi + 30 * 60_000) / HOUR) * HOUR);
  if (hi - lo < 2 * HOUR) hi = Math.min(dayEnd, lo + 2 * HOUR);
  const span = Math.max(1, hi - lo);
  const pct = (t: number) => Math.max(0, Math.min(100, ((t - lo) / span) * 100));

  const toBlock = (b: WorkspaceBlockDto): TimelineBlock | null => {
    const s = ms(b.start_at);
    if (s == null) return null;
    let e = b.end_at ? ms(b.end_at)! : b.kind === 'planned' ? null : nowMs;
    if (e == null) return null; // a planned window with no stored finish: unknown, not drawn as a guess
    if (e < s) e = s;
    const startC = Math.max(s, lo);
    const endC = Math.min(e, hi);
    const ticket = b.ticket_number != null ? `TKT-${String(b.ticket_number).padStart(4, '0')}` : 'ticket';
    const what = b.kind === 'unattended' && b.unattended_kind ? ` (${b.unattended_kind.replace(/_/g, ' ').toLowerCase()})` : '';
    const act = b.activity === 'inspection' ? ' inspection' : '';
    const label = `${KIND_WORDS[b.kind]}${act}${what}. ${ticket}${b.vehicle_label ? `, ${b.vehicle_label}` : ''}. `
      + `${b.kind === 'planned' ? 'Planned' : 'Started'} ${formatShopTime(s, tz)}, `
      + (b.open ? 'still running' : `${b.kind === 'planned' ? 'planned finish' : 'ended'} ${formatShopTime(e, tz)}`)
      + (b.expected_end_at ? `, expected end ${formatShopTime(ms(b.expected_end_at)!, tz)} (stated)` : '')
      + (b.kind !== 'planned' ? `, ${Math.max(0, Math.round((e - s) / 60_000))} minutes` : '') + '.';
    return {
      id: b.id,
      kind: b.kind,
      ticketId: b.ticket_id,
      startMs: s,
      endMs: e,
      open: b.open,
      timeBasis: b.time_basis,
      laborCredit: b.labor_credit === true && b.kind === 'hands_on',
      leftPct: pct(startC),
      widthPct: Math.max(0.6, pct(endC) - pct(startC)),
      label,
      expectedEndMs: ms(b.expected_end_at),
      conflict: false,
    };
  };

  const laborRow = blocks.filter((b) => b.kind === 'hands_on').map(toBlock).filter((b): b is TimelineBlock => !!b)
    .sort((a, b) => a.startMs - b.startMs);
  let laborOverlapDetected = false;
  for (let i = 1; i < laborRow.length; i++) {
    if (laborRow[i].startMs < laborRow[i - 1].endMs) {
      laborRow[i].conflict = true;
      laborRow[i - 1].conflict = true;
      laborOverlapDetected = true;
    }
  }

  const bays = dto.bays ?? {};
  const lanes: VehicleLane[] = vehicles.map((v) => ({
    ticketId: v.ticket_id,
    ticketNumber: v.ticket_number,
    vehicleLabel: v.vehicle_label,
    bayName: v.bay_id ? bays[v.bay_id]?.name ?? null : null,
    bayBasis: v.bay_basis,
    state: v.state,
    stateLabel: vehicleStateLabel(v.state),
    predictedFinishMs: v.predicted_finish_basis === 'predicted' ? ms(v.predicted_finish_at) : null,
    blocks: blocks.filter((b) => b.ticket_id === v.ticket_id).map(toBlock).filter((b): b is TimelineBlock => !!b)
      .sort((a, b) => a.startMs - b.startMs),
    openUnattended: v.open_unattended ?? [],
  }));
  // Live first (hands-on, then machine, then waiting), then the rest in planned order.
  const rank = (s: string) => (s === 'HANDS_ON' ? 0 : s === 'UNATTENDED_RUNNING' ? 1 : s === 'SWITCHED_AWAY' ? 2 : s.startsWith('PAUSED_') ? 3 : 4);
  lanes.sort((a, b) => rank(a.state) - rank(b.state));

  const physical = vehicles.filter((v) => v.bay_id && (v.bay_basis === 'physical_current' || v.bay_basis === 'legacy_projection')
    && v.state !== 'NOT_STARTED' && v.state !== 'DONE');
  const ticks: WorkspaceTimeline['ticks'] = [];
  for (let t = Math.ceil(lo / HOUR) * HOUR; t <= hi; t += HOUR) ticks.push({ ms: t, label: formatShopTime(t, tz), leftPct: pct(t) });

  return {
    axisStartMs: lo,
    axisEndMs: hi,
    ticks,
    nowMs,
    nowLeftPct: nowMs >= lo && nowMs <= hi ? pct(nowMs) : null,
    laborRow,
    lanes,
    vehiclesInBays: physical.length,
    distinctBays: new Set(physical.map((v) => v.bay_id)).size,
    laborOverlapDetected,
  };
}

/** Minutes as "1h 05m" / "35m"; unknown stays unknown ("—"). */
export function formatMinutes(min: number | null | undefined): string {
  if (min == null || !Number.isFinite(min)) return '—';
  const m = Math.max(0, Math.round(min));
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`;
}
